import React from "react";
import { useApplicationStatus } from "../context/ApplicationStatusContext";
import { Link } from "react-router-dom";

/**
 * A reusable Apply button that is automatically disabled when applications are closed
 *
 * @param {Object} props - Component props
 * @param {string} props.to - URL to navigate to when clicked
 * @param {string} props.className - Button styling classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Optional click handler
 * @returns {React.ReactElement} - The Apply button component
 */
const ApplyButton = ({
  to = "/login",
  className = "",
  children = "Apply Now",
  onClick,
  ...props
}) => {
  const { isApplicationOpen, checkingStatus } = useApplicationStatus();

  if (checkingStatus) {
    // Show loading state while checking application status
    return (
      <button className={className} disabled {...props}>
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Loading...
      </button>
    );
  }

  // If applications are closed, render a button that shows tooltip but maintains styling
  if (!isApplicationOpen) {
    return (
      <div className="relative group">
        <button className={className} disabled {...props}>
          {children}
        </button>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-48 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Applications are currently closed
        </div>
      </div>
    );
  }

  // If applications are open and there's an onClick handler, use a button
  if (onClick) {
    return (
      <button className={className} onClick={onClick} {...props}>
        {children}
      </button>
    );
  }

  // Otherwise, use a Link (default behavior)
  return (
    <Link to={to} className={className} {...props}>
      {children}
    </Link>
  );
};

export default ApplyButton;
